/**
 * @file add-row-button.component.tsx
 * @description Компонент для отображения кнопки добавления новой строки в таблицу.
 * @author AI-Generated | Human-Reviewed
 * @created 2025-01-15
 * @updated 2025-01-16
 * @dependencies ['react', '@consta/icons/IconAdd', '@components/ui/base/button', '@consta/icons/IconConnection', '@components/icons/attach-file', '@forms/modals/link.modal', '@api/actions/file', '@features/catalog', '@utils/data-helper']
 * @related-files ['src/components/icons/attach-file', 'src/forms/modals/link.modal.tsx', 'src/api/actions/file.ts', 'src/features/catalog', 'src/types/attachment.ts']
 */

import { Button } from '@components/ui/base/button';
import { IconAdd } from '@consta/icons/IconAdd';
import { useMemo, useState } from 'react';
import { ObjectForm } from '@src/features/object-form';
import { getObjectEntity } from '@utils/form-object-helper';
import cn from 'classnames';
import { useDetectClickOutside } from 'react-detect-click-outside';
import { Form } from '@forms/default-object';
import { Form as DefaultGroupForm } from '@forms/default-group';
import { Catalog } from '@features/catalog';
import { IBaseObjectDTO, ITableItemDTO, TBaseObjectDto } from '@src/types';

/**
 * Props для компонента кнопки добавления строки в таблицу
 */
interface IAddRowButtonProps {
    /** Тип объекта для создания */
    type: string;
    /** Позиционирование справа в пикселях */
    right?: string;
    /** Позиционирование снизу в пикселях */
    bottom?: string;
    /** Данные для инициализации нового объекта */
    initialItem?: IBaseObjectDTO;
    /** Данные для инициализации нового объекта */
    currentItems?: ITableItemDTO[];
    /** Разрешить выбор из каталога вместо создания нового */
    isAddFromCatalog?: boolean;
    /** Дополнительная кнопка добавления */
    isAdditional?: boolean;
    /** Callback функция при обновлении данных */
    onDataUpdate?: (newItem: IBaseObjectDTO) => void;
}

export const AddRowButton = ({
    type,
    initialItem,
    currentItems,
    onDataUpdate,
    isAddFromCatalog = false,
    isAdditional = false,
    right = '4.2342vmin',
    bottom = '4.2342vmin'
}: IAddRowButtonProps) => {
    const [isClicked, setIsClicked] = useState(false);
    const ref = useDetectClickOutside({ onTriggered: () => setIsClicked(false) });
    const [isShowModal, setIsShowModal] = useState(false);
    const [isGroupForm, setIsGroupForm] = useState(false);
    const entity = useMemo<TBaseObjectDto>(() => getObjectEntity(type), [type]);
    const objectForm = useMemo(() => entity?.form || Form, [entity]);
    const groupForm = useMemo(() => entity?.groupForm || DefaultGroupForm, [entity]);

    if (!entity || !entity.type) {
        return;
    }

    return (
        <>
            <div ref={ref} className="additional-buttons fixed z-[100]">
                <div>
                    <div
                        className={cn('transition-all invisible opacity-0 fixed', {
                            'bottom-[10.6261vmin] !opacity-100 !visible': isClicked
                        })}
                        style={{ right }}
                    >
                        <Button
                            label="Добавить элемент"
                            size="s"
                            iconSize="s"
                            form="round"
                            onClick={() => {
                                setIsGroupForm(false);
                                setIsShowModal(true);
                                setIsClicked(false);
                            }}
                            iconRight={IconAdd}
                        />
                    </div>

                    <div
                        className={cn('transition-all invisible opacity-0 fixed', {
                            'bottom-[15.3803vmin] !opacity-100 !visible': isClicked
                        })}
                        style={{ right }}
                    >
                        <Button
                            label="Добавить группу"
                            size="s"
                            iconSize="s"
                            form="round"
                            onClick={() => {
                                setIsGroupForm(true);
                                setIsShowModal(true);
                                setIsClicked(false);
                            }}
                            iconRight={IconAdd}
                        />
                    </div>
                </div>

                <Button
                    size="l"
                    form="round"
                    onClick={() =>
                        isAdditional && entity.parentType ? setIsClicked(!isClicked) : setIsShowModal(true)
                    }
                    iconLeft={IconAdd}
                    className={cn('add-row-button transition-transform fixed rounded-full', {
                        'rotate-45': isClicked
                    })}
                    style={{ right, bottom }}
                />
            </div>

            {isShowModal && !isAddFromCatalog && (
                <ObjectForm
                    {...entity}
                    tabs={isGroupForm ? null : entity.tabs}
                    type={isGroupForm && entity.parentType ? entity.parentType : entity.type}
                    form={isGroupForm ? groupForm : objectForm}
                    initialItem={initialItem || {}}
                    onChange={(newItem: IBaseObjectDTO) => onDataUpdate && onDataUpdate(newItem)}
                    onClose={() => setIsShowModal(false)}
                />
            )}

            {isShowModal && isAddFromCatalog && (
                <Catalog
                    onRowClick={(newItem: IBaseObjectDTO) => {
                        onDataUpdate && onDataUpdate(newItem);
                        setIsShowModal(false);
                    }}
                    type={type}
                    currentItems={currentItems}
                    onClose={() => setIsShowModal(false)}
                />
            )}
        </>
    );
};